import { create } from 'zustand';

import { normalizeError } from '../api/core/errors';
import { QuestionBankRepository } from '../api/repository/question-bank';

import type { ViewOnlyQuestion } from './viewOnly';
import { fetchBoardMade, isLegacyMigrated, markLegacyMigrated, pickUnsynced, readLegacyItems } from './legacy';
import { hiddenTypesMessage, partialSaveMessage, sessionMessages } from './messages';
import { currentAccess, failure, reason, sessionRemedy, setupError } from './requestState';
import { DEFAULT_PAGE_SIZE, bankTab, clampPage, serverFilter } from './paging';
import { bankChanged, readPage } from './pageReads';
import { batchProblem, payloadProblem, toPayload } from './payload';
import type { QuestionBankPayload } from './payload';
import { findItem, mergePatch, prependItems, removeIds, replaceItems } from './reducers';
import type {
  QuestionBankFilter,
  QuestionBankInput,
  QuestionBankItem,
  QuestionBankLessonRef,
  QuestionBankPatch,
} from './types';

/**
 * The school's question bank, served by IZTeach examination-service /question (tenant-wide).
 *
 * Every write is a request, so every action is async and reports whether it landed. Writes go out
 * one at a time, never in parallel, so a bulk action does not fan out across the Lambda. The list is
 * one server page: the filter, page and page size go to /question/admin (paging.ts), so changing any
 * of them asks for another page; pageReads.ts reads it and the filter's count apart from each other.
 */

const NOT_FOUND = 'Không tìm thấy câu hỏi này trong kho bài tập.';

export interface LegacyImportResult {
  saved: number;
  /** Local questions the server would refuse; left on the device rather than dropped. */
  skipped: number;
}

export interface QuestionBankState {
  /** The current server page, minus rows of types the board cannot show. */
  items: QuestionBankItem[];
  filter: QuestionBankFilter;
  /** 1-based. */
  page: number;
  pageSize: number;
  /** /question/count for the filter; null while it is out or when it failed. */
  total: number | null;
  /** Rows the server sent for this page, hidden types included. */
  fetched: number;
  /** A request is in flight. */
  loading: boolean;
  /** True once a load has succeeded, so an empty bank reads differently from an unasked one. */
  loaded: boolean;
  /** The last failure, already in the teacher's words. */
  error: string | null;
  /** The last failure was the network itself rather than the server refusing. */
  offline: boolean;
  /** Questions still only on this device, waiting for the teacher to decide. */
  legacyCount: number;
  /** No usable IZTeach teacher session - the page points at /setup instead of the list. */
  needsSetup: boolean;
  /** Rows of types the board cannot show, left out of `items` and said so in `hiddenNote`. */
  hiddenNote: string | null;
  /** Those same rows, listed read-only so the page shows every row it counts. */
  others: ViewOnlyQuestion[];

  /** `recount` drops the stored count too - for a fresh visit, a retry, or rows changed elsewhere. */
  load: (options?: { recount?: boolean }) => Promise<QuestionBankItem[]>;
  addMany: (items: QuestionBankInput[]) => Promise<string[]>;
  addOne: (item: QuestionBankInput) => Promise<string | null>;
  update: (id: string, patch: QuestionBankPatch) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;
  setFilter: (filter: QuestionBankFilter) => void;
  clearFilter: () => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  attachLesson: (ids: string[], lesson: QuestionBankLessonRef) => Promise<boolean>;
  detachLesson: (ids: string[]) => Promise<boolean>;
  importLegacy: () => Promise<LegacyImportResult | null>;
  dismissLegacy: () => void;
  clearError: () => void;
}

/** Promise.allSettled is not on the Chrome 58 panels, so each request reports itself. */
function settled<T>(task: Promise<T>): Promise<{ value: T | null; error: unknown }> {
  return task.then(
    (value) => ({ value, error: null as unknown }),
    (error: unknown) => ({ value: null as T | null, error }),
  );
}

/** Filters and pages can be tapped while a load is still out, so only the newest answer may land. */
let loadToken = 0;

/** Local questions the school bank lacks, deduped against every board-made question of the vendor. */
function lookupLegacy(): Promise<QuestionBankItem[]> {
  return fetchBoardMade((query) => QuestionBankRepository.list(query)).then((rows) => pickUnsynced(readLegacyItems(), rows));
}

/** The session's answer to that lookup, read once; a failed lookup is kept as null, never retried per page. */
let legacyLookup: Promise<QuestionBankItem[] | null> | null = null;

/** How many local questions to offer, or 0 when that cannot be known - never a guess that over-counts. */
function pendingLegacyCount(): Promise<number> {
  if (isLegacyMigrated() || readLegacyItems().length === 0) return Promise.resolve(0);
  if (!legacyLookup) legacyLookup = lookupLegacy().catch(() => null);
  return legacyLookup.then((pending) => (pending ? pending.length : 0));
}

/** The body for a PUT, plus the reason the server would refuse it. */
function prepare(item: QuestionBankItem, patch: QuestionBankPatch): { payload: QuestionBankPayload; problem: string | null } {
  const payload = toPayload(mergePatch(item, patch));
  return { payload, problem: payloadProblem(payload) };
}

export const useQuestionBankStore = create<QuestionBankState>((set, get) => {
  /** False, with the /setup message set, when there is no teacher session to call the bank with. */
  function ready(): boolean {
    const access = currentAccess();
    if (access.status === 'ok') return true;
    set({ error: setupError(access), offline: false, needsSetup: true, loading: false });
    return false;
  }

  /**
   * One POST per question, in order: there is no batch endpoint and this is not atomic. It stops at
   * the first failure, so everything before it is saved and everything after it was never sent.
   */
  async function createAll(payloads: QuestionBankPayload[]): Promise<{ created: QuestionBankItem[]; error: string | null; offline: boolean }> {
    const created: QuestionBankItem[] = [];
    for (let i = 0; i < payloads.length; i += 1) {
      try {
        created.push(await QuestionBankRepository.create(payloads[i]));
      } catch (error) {
        const message = payloads.length === 1 ? reason(error) : partialSaveMessage(created.length, payloads.length, reason(error));
        return { created, error: message, offline: normalizeError(error).kind === 'network' };
      }
    }
    return { created, error: null, offline: false };
  }

  /** One request per question, in order, each reporting itself - the API has no bulk update or delete. */
  async function eachInTurn<T>(list: T[], task: (entry: T) => Promise<unknown>): Promise<{ entry: T; value: unknown; error: unknown }[]> {
    const results: { entry: T; value: unknown; error: unknown }[] = [];
    for (let i = 0; i < list.length; i += 1) {
      const result = await settled(task(list[i]));
      results.push({ entry: list[i], value: result.value, error: result.error });
    }
    return results;
  }

  /** One PUT per question: the API has no bulk update, and the selection bounds how many. */
  async function applyBulk(ids: string[], patch: QuestionBankPatch): Promise<boolean> {
    if (ids.length === 0) return true;
    const targets = ids
      .map((id) => findItem(get().items, id))
      .filter((item): item is QuestionBankItem => item !== null);
    if (targets.length === 0) {
      set({ error: NOT_FOUND, offline: false });
      return false;
    }
    const prepared = targets.map((item) => prepare(item, patch));
    const refused = prepared.filter((entry) => entry.problem !== null)[0];
    if (refused) {
      set({ error: refused.problem, offline: false });
      return false;
    }

    if (!ready()) return false;
    set({ loading: true, error: null, offline: false });
    const results = await eachInTurn(targets.map((item, index) => ({ item, payload: prepared[index].payload })), (entry) =>
      QuestionBankRepository.update(entry.item, entry.payload),
    );
    const saved = results
      .map((result) => result.value as QuestionBankItem | null)
      .filter((item): item is QuestionBankItem => item !== null);
    const items = replaceItems(get().items, saved);
    if (saved.length > 0) bankChanged();
    const failed = results.filter((result) => result.error !== null)[0];
    if (failed) {
      set({ items, loading: false, ...failure(failed.error) });
      return false;
    }
    set({ items, loading: false });
    return true;
  }

  return {
    items: [],
    filter: {},
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: null,
    fetched: 0,
    loading: false,
    loaded: false,
    error: null,
    offline: false,
    legacyCount: 0,
    needsSetup: false,
    hiddenNote: null,
    others: [],

    async load(options) {
      if (options && options.recount) bankChanged();
      const access = currentAccess();
      if (access.status !== 'ok') {
        loadToken += 1;
        set({ items: [], loaded: false, loading: false, needsSetup: true, hiddenNote: null, others: [], error: setupError(access), offline: false });
        return [];
      }
      // "Của tôi" with no id to ask by would quietly show the whole school instead
      if (get().filter.mine && !access.userId) {
        set({ error: sessionMessages(sessionRemedy()).mineUnknown, offline: false });
        return get().items;
      }
      const filter = serverFilter(get().filter, access.userId);
      const { page, pageSize } = get();
      const token = (loadToken += 1);
      const read = readPage(filter, bankTab(get().filter) === 'manual', page, pageSize);
      set(read.fresh
        ? { loading: true, error: null, offline: false, needsSetup: false, total: null }
        : { loading: true, error: null, offline: false, needsSetup: false });
      // the count lands on its own; the page never waits for it
      if (read.fresh) void read.total.then((total) => { if (read.isCurrent()) set({ total }); });
      try {
        const rows = await read.rows(read.fresh ? null : get().total);
        const batch = await QuestionBankRepository.toPage(rows);
        if (token !== loadToken) return batch.items;
        // a page past the end (the last row was deleted, or another teacher trimmed the bank) steps back
        if (batch.fetched === 0 && page > 1) {
          const total = await read.total;
          if (token !== loadToken) return batch.items;
          if (total !== null && page > clampPage(page, total, pageSize)) {
            set({ page: clampPage(page, total, pageSize) });
            return get().load();
          }
        }
        const hidden = batch.others.filter((other) => other.kind === 'other').length;
        set({
          items: batch.items,
          others: batch.others,
          fetched: batch.fetched,
          loaded: true,
          loading: false,
          hiddenNote: hidden > 0 ? hiddenTypesMessage(hidden) : null,
        });
        // read once per session, apart from the page; a late reply must not undo Bỏ qua or an upload
        void pendingLegacyCount().then((legacyCount) => set({ legacyCount: isLegacyMigrated() ? 0 : legacyCount }));
        return batch.items;
      } catch (error) {
        if (token !== loadToken) return [];
        set({ loading: false, ...failure(error) });
        return [];
      }
    },

    /**
     * The AI import path. Returns the ids the SERVER assigned, in request order, and only for the
     * questions that actually landed - a partial result is what the caller highlights.
     */
    async addMany(input) {
      if (input.length === 0) return [];
      const payloads = input.map((item) => toPayload({ ...item, source: 'ai-quiz-supporter' }));
      // checked whole before the first POST, so a bad question never leaves the import half-saved
      const problem = batchProblem(payloads);
      if (problem) {
        set({ error: problem, offline: false });
        return [];
      }
      if (!ready()) return [];

      set({ loading: true, error: null, offline: false });
      const result = await createAll(payloads);
      if (result.created.length > 0) bankChanged();
      const items = prependItems(get().items, result.created);
      if (result.error) {
        set({ items, loading: false, error: result.error, offline: result.offline });
        return result.created.map((item) => item.id);
      }
      set({ items, loading: false });
      return result.created.map((item) => item.id);
    },

    async addOne(item) {
      const payload = toPayload({ ...item, source: 'manual' });
      const problem = payloadProblem(payload);
      if (problem) {
        set({ error: problem, offline: false });
        return null;
      }
      if (!ready()) return null;
      set({ loading: true, error: null, offline: false });
      try {
        const created = await QuestionBankRepository.create(payload);
        bankChanged();
        set({ items: prependItems(get().items, [created]), loading: false });
        return created.id;
      } catch (error) {
        set({ loading: false, ...failure(error) });
        return null;
      }
    },

    async update(id, patch) {
      const current = findItem(get().items, id);
      if (!current) {
        set({ error: NOT_FOUND, offline: false });
        return false;
      }
      const prepared = prepare(current, patch);
      if (prepared.problem) {
        set({ error: prepared.problem, offline: false });
        return false;
      }
      if (!ready()) return false;
      set({ loading: true, error: null, offline: false });
      try {
        const saved = await QuestionBankRepository.update(current, prepared.payload);
        bankChanged();
        set({ items: replaceItems(get().items, [saved]), loading: false });
        return true;
      } catch (error) {
        set({ loading: false, ...failure(error) });
        return false;
      }
    },

    async remove(id) {
      if (!ready()) return false;
      set({ loading: true, error: null, offline: false });
      try {
        await QuestionBankRepository.remove(id);
        bankChanged();
        set({ items: removeIds(get().items, [id]), loading: false });
        // the next page's first row moves up, and the count drops
        void get().load();
        return true;
      } catch (error) {
        set({ loading: false, ...failure(error) });
        return false;
      }
    },

    async removeMany(ids) {
      if (ids.length === 0) return true;
      if (!ready()) return false;
      set({ loading: true, error: null, offline: false });
      const results = await eachInTurn(ids, (id) => QuestionBankRepository.remove(id));
      // whatever went through is gone on the server, so the list drops it even if a sibling failed
      const gone = results.filter((result) => result.error === null).map((result) => result.entry);
      if (gone.length > 0) bankChanged();
      const items = removeIds(get().items, gone);
      const failed = results.filter((result) => result.error !== null)[0];
      if (failed) {
        set({ items, loading: false, ...failure(failed.error) });
        return false;
      }
      set({ items, loading: false });
      void get().load();
      return true;
    },

    setFilter(filter) {
      set({ filter: { ...get().filter, ...filter }, page: 1 });
      void get().load();
    },

    clearFilter() {
      // the tab picks the bank; clearing the facets keeps it
      set({ filter: { source: bankTab(get().filter) }, page: 1 });
      void get().load();
    },

    setPage(page) {
      set({ page: Math.max(1, Math.floor(page)) });
      void get().load();
    },

    setPageSize(size) {
      set({ pageSize: size, page: 1 });
      void get().load();
    },

    attachLesson(ids, lesson) {
      return applyBulk(ids, { lessonRef: lesson });
    },

    detachLesson(ids) {
      return applyBulk(ids, { lessonRef: undefined });
    },

    /**
     * Upload the device-local bank, on the teacher's say-so. Safe to run twice: a fresh lookup
     * drops anything the school bank already holds, and the flag stops the offer coming back.
     * Without a complete lookup it refuses rather than risk duplicates.
     */
    async importLegacy() {
      if (!ready()) return null;
      set({ loading: true, error: null, offline: false });
      let pending: QuestionBankItem[];
      try {
        pending = await lookupLegacy();
      } catch (error) {
        legacyLookup = null;
        set({ loading: false, legacyCount: 0, ...failure(error) });
        return null;
      }
      if (pending.length === 0) {
        markLegacyMigrated();
        set({ loading: false, legacyCount: 0 });
        return { saved: 0, skipped: 0 };
      }

      const payloads: QuestionBankPayload[] = [];
      let skipped = 0;
      pending.forEach((item) => {
        const payload = toPayload(item);
        // one unsalvageable old question must not block the rest of the upload
        if (payloadProblem(payload)) {
          skipped += 1;
          return;
        }
        payloads.push(payload);
      });

      const result = await createAll(payloads);
      if (result.created.length > 0) bankChanged();
      const items = prependItems(get().items, result.created);
      // the lookup just made, minus what landed, is what is still missing
      const left = pickUnsynced(pending, result.created);
      legacyLookup = Promise.resolve(left);
      if (result.error) {
        set({ items, loading: false, legacyCount: left.length, error: result.error, offline: result.offline });
        return null;
      }
      // the old key is left untouched; only this flag retires the offer
      markLegacyMigrated();
      set({ items, loading: false, legacyCount: 0 });
      // the uploads change this filter's page and count
      if (result.created.length > 0) void get().load();
      return { saved: result.created.length, skipped };
    },

    dismissLegacy() {
      markLegacyMigrated();
      set({ legacyCount: 0 });
    },

    clearError: () => set({ error: null, offline: false }),
  };
});
